import {css, StyleSheet} from 'aphrodite';
import reactUpdate from 'immutability-helper';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import type {CreatedParentMolecule} from 'chemistry/molecules/MoleculeDAO';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {openCreateMoleculeModal} from 'nav/CreateDropdownActions';
import {filterReactionConditionParameterDefinitions} from 'notebook/integrations/chemistry/filterReactionConditionParameterDefinitions';
import ResyncReactionTableBanner, {
  RESYNC_BANNER_BUTTON_TEXT,
  RESYNC_BANNER_SUBTITLE,
  RESYNC_BANNER_TITLE,
} from 'notebook/integrations/chemistry/reaction-scheme/ResyncReactionTableBanner';
import StoichiometryTableEmbeddedChemicalSketcher from 'notebook/integrations/chemistry/reaction-scheme/StoichiometryTableEmbeddedChemicalSketcher';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import SelectableNotePart from 'notebook/mediocre/selection/block/SelectableNotePart';
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import {refreshNoteLinkedGroup} from 'notebook/notes/groups/action-functions/refreshNoteLinkedGroupActionFunction';
import GroupTitleBar from 'notebook/notes/groups/GroupTitleBar';
import {getStoichiometryGroupAdapter} from 'notebook/notes/groups/NoteLinkedGroupAdapters';
import {
  GroupActionFunctionParameters,
  StructuredTableSpreadsheetProps,
} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {reduceGroupUpdateNotePartItem} from 'notebook/notes/groups/util/ContainedNotePartUtil';
import {getContainedTableSearchResultProps} from 'notebook/notes/groups/util/getContainedTableSearchResultProps';
import groupKindToIcon from 'notebook/notes/groups/util/groupKindToIcon';
import maybeAsyncUpdateContainedNotePart from 'notebook/notes/groups/util/maybeAsyncUpdateContainedNotePart';
import {makeTableItemPropsByName} from 'notebook/notes/groups/util/NoteLinkedGroupViewSpreadsheetProps';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {generateNoteLinkedObjectCollapseId} from 'notebook/notes/util/generateNLCollapseId';
import {
  ActionFunctionContext,
  useActionFunctionContext,
} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import StructuredTableFormOrView from 'notebook/structured-tables/StructuredTableFormOrView';
import {NoteLinkedStructuredTable} from 'notebook/structured-tables/StructuredTablePlugin';
import {useNotebookItemCollapse} from 'notebook/tables/collapse/useNotebookItemCollapse';
import {type TableOpDiffMap} from 'notebook/tables/excess/ops/TableOpConstants';
import {NoteLinkedFile, NotePart} from 'notebook/util/NoteUtilConstants';
import {
  LibraryParameterDefinitionsContext,
  useAllLibraryParameterDefinitions,
} from 'procedures/parameter-library/LibraryParameterDefinitionsContext';
import {GRAY} from 'ui-library/Colors';
import OverflowMenu from 'ui-library/menus/OverflowMenu';
import memoizeLastCall from 'util/memoizeLastCall';

import attachUnrecognizedMoleculeResolutionActions, {
  type InitialChemicalStructureForUnrecognizedMolecule,
} from './attachUnrecognizedMoleculeResolutionActions';
import {
  makeEmptyReactionSchemeFile,
  StoichiometryCreateGroupExtras,
  StoichiometryGroup,
  StoichiometryGroupMetadata,
} from './StoichiometryNoteLinkedGroupAdapter';
import {StoichiometryStructuredTable} from './StoichiometryStructuredTableAdapter';
import {updateStoichiometryTableWithCreatedMoleculeAndParent} from './updateStoichiometryTableWithCreatedMolecule';

export interface StoichiometryGroupViewProps extends Partial<MediocreSearchResultProps> {
  notePartCid: string;
  group: StoichiometryGroup;
  editor: NoteEditor;
  editorProps: NoteEditorProps;
  spreadsheetProps: StructuredTableSpreadsheetProps;
}

interface ActionFunctionContextProps {
  actionFunctionContext: ActionFunctionContext;
}

interface StoichiometryGroupInnerViewState {
  hasLiveStaleCells: boolean;
  hasReceivedLiveStalenessResult: boolean;
  diagramSyncing: boolean;
}

export {RESYNC_BANNER_BUTTON_TEXT, RESYNC_BANNER_SUBTITLE, RESYNC_BANNER_TITLE};

function GroupActionButtons<TGroupMetadata extends {}, TCreateGroupExtras extends {}>(
  props: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>
): JSX.Element {
  const actionFunctions = props.noteLinkedGroupAdapter.getActionFunctions();

  // When collapsed, only show the primary action function.
  return (
    <>
      {!props.isCollapsed &&
        actionFunctions.secondary?.map((actionFunction, index) => (
          <React.Fragment key={index}>{actionFunction.renderToolbarButton(props)}</React.Fragment>
        ))}
      {!props.isCollapsed && actionFunctions.overflow?.length ? (
        <OverflowMenu
          options={actionFunctions.overflow
            .map((actionFunction) => actionFunction.getOverflowMenuItem(props))
            .filter((option) => option != null)}
        />
      ) : null}
      {actionFunctions.primary?.renderToolbarButton(props)}
    </>
  );
}

type StoichiometryGroupInnerViewProps = StoichiometryGroupViewProps &
  ActionFunctionContextProps & {
    isCollapsed: boolean;
    onToggleCollapsed: () => void;
  };

class StoichiometryGroupInnerView extends React.Component<
  StoichiometryGroupInnerViewProps,
  StoichiometryGroupInnerViewState
> {
  override state: StoichiometryGroupInnerViewState = {
    hasLiveStaleCells: false,
    hasReceivedLiveStalenessResult: false,
    diagramSyncing: false,
  };

  private emptyReactionSchemeFile: NoteLinkedFile | null = null;
  private emptyReactionSchemeFileName: string | null = null;

  private getOrCreateEmptyReactionSchemeFile(name: string): NoteLinkedFile {
    if (this.emptyReactionSchemeFile == null || this.emptyReactionSchemeFileName !== name) {
      this.emptyReactionSchemeFile = makeEmptyReactionSchemeFile(name);
      this.emptyReactionSchemeFileName = name;
    }
    return this.emptyReactionSchemeFile;
  }

  handleLiveStalenessChange = (hasStaleCells: boolean): void => {
    if (hasStaleCells !== this.state.hasLiveStaleCells || !this.state.hasReceivedLiveStalenessResult) {
      this.setState({hasLiveStaleCells: hasStaleCells, hasReceivedLiveStalenessResult: true});
    }
  };

  handleDiagramSyncingChange = (diagramSyncing: boolean): void => {
    if (diagramSyncing !== this.state.diagramSyncing) {
      this.setState({diagramSyncing});
    }
  };

  // The table (and its live-data polling) unmounts entirely while collapsed -- see the
  // `isExpanded &&` guard in render() -- so no further staleness results will arrive until it's
  // expanded again. Reset to "unknown" on collapse so re-expanding always waits for a fresh result
  // rather than trusting a result that may now be out of date.
  override componentDidUpdate(prevProps: Readonly<StoichiometryGroupInnerViewProps>): void {
    if (!prevProps.isCollapsed && this.props.isCollapsed && this.state.hasReceivedLiveStalenessResult) {
      this.setState({hasReceivedLiveStalenessResult: false});
    }
  }

  handleDeleteGroup = (): void => {
    const {editor, notePartCid} = this.props;
    const parentIndex = editor.getIndex(notePartCid);
    assert(parentIndex !== -1, `Could not find note part with cid ${notePartCid}`);
    editor.itemDeleteHandler(parentIndex);
  };

  handleUpdate = (updatedFile: NoteLinkedFile, updatedTable: StoichiometryStructuredTable): void => {
    const {group, editor, editorProps, notePartCid} = this.props;
    const adapter = getStoichiometryGroupAdapter();
    const reactionSchemePart = adapter.getReactionSchemeNotePart(group);
    const stoichiometryTablePart = adapter.getStoichiometryTableNotePart(group);
    // To save a file, we need its real server id. We look it up by the file's cid.
    // - New file from the server: the cid is the real id, so record it.
    // - File loaded with the page: the cid is a temporary label like "note-linked-file-24", and the
    //   real id is already recorded. Recording the label as the id would send it to the server, and
    //   the server would reject every save.
    if (updatedFile.cid != null && editorProps.getIdFromCid(updatedFile.cid) == null) {
      editorProps.registerObjects({[updatedFile.cid]: updatedFile.cid});
    }
    let updatedGroup: StoichiometryGroup = group;
    if (reactionSchemePart != null) {
      updatedGroup = reduceGroupUpdateNotePartItem(updatedGroup, reactionSchemePart, updatedFile);
    } else {
      // No NoteLinkedFile part exists yet — add one to the group.
      const newReactionSchemePart = reactUpdate(makeDefaultItem(ModelTypeConstants.NOTE_LINKED_OBJECT), {
        item: {$set: updatedFile},
      });
      updatedGroup = reactUpdate(updatedGroup, {
        containedNoteParts: {$unshift: [newReactionSchemePart]},
      });
    }
    updatedGroup = reduceGroupUpdateNotePartItem(updatedGroup, stoichiometryTablePart, updatedTable);
    editor.itemUpdateHandler(editor.getIndex(notePartCid), {item: updatedGroup});
  };

  getUnrecognizedMoleculeResolutionAction =
    (
      canonicalSmiles: string,
      initialChemicalStructure: InitialChemicalStructureForUnrecognizedMolecule,
      initialName?: string
    ) =>
    () => {
      openCreateMoleculeModal({
        initialFolderId: null,
        initialChemicalStructure,
        initialName,
        onMoleculeCreated: async (
          moleculeId: string,
          moleculeName: string,
          parentMolecule?: CreatedParentMolecule
        ) => {
          const {group, editor, notePartCid} = this.props;
          const adapter = getStoichiometryGroupAdapter();
          const stoichiometryTablePart = adapter.getStoichiometryTableNotePart(group);
          const updatedTable = updateStoichiometryTableWithCreatedMoleculeAndParent(
            stoichiometryTablePart.item,
            {canonicalSmiles, id: moleculeId, name: moleculeName},
            parentMolecule
          );
          if (updatedTable == null) {
            return;
          }
          const updatedContainedNotePart = {...stoichiometryTablePart, item: updatedTable};
          await maybeAsyncUpdateContainedNotePart(group, editor, notePartCid, updatedContainedNotePart);
        },
      });
    };

  handleUpdateContainedTableContent = async (
    containedNotePartIndex: number,
    content: object,
    _selectors?: never,
    tableOp?: TableOpDiffMap
  ): Promise<void> => {
    const {group, editor, notePartCid} = this.props;
    const oldContainedNotePart = group.containedNoteParts[containedNotePartIndex];
    const updatedContainedNotePart = editor.getItemWithUpdatedContent(oldContainedNotePart, content);
    await maybeAsyncUpdateContainedNotePart(group, editor, notePartCid, updatedContainedNotePart, tableOp);
  };

  _getStableTableItemProps = memoizeLastCall(makeTableItemPropsByName);

  _enrichResolutionActions = memoizeLastCall(attachUnrecognizedMoleculeResolutionActions);

  override render(): JSX.Element {
    const {
      group,
      editor,
      editorProps,
      notePartCid,
      spreadsheetProps,
      actionFunctionContext,
      isCollapsed,
      onToggleCollapsed,
    } = this.props;
    const adapter = getStoichiometryGroupAdapter();
    const {evaluatedTableMap, tableItemPropsByName} = this._getStableTableItemProps(
      group,
      editor,
      spreadsheetProps,
      editor.props.editingTable,
      editor.props.computedTables
    );

    const reactionSchemePart = adapter.getReactionSchemeNotePart(group);
    const stoichiometryTablePart = adapter.getStoichiometryTableNotePart(group);
    const stoichiometryTable = stoichiometryTablePart.item;
    const stoichiometryTableAdapter = getStructuredTableAdapters()[stoichiometryTable.kind];
    const stoichiometryTableData = stoichiometryTableAdapter.toTable(stoichiometryTable);
    const stoichiometryComputedTable = evaluatedTableMap[stoichiometryTableData.name];
    const stoichiometryTableItemProps = tableItemPropsByName[stoichiometryTableData.name];

    // The Reaction Conditions table is currently only added when REACTION_PARAMETERS_ENABLED is set.
    const reactionConditionsTablePart = adapter.getReactionConditionsTableNotePart(group);
    const reactionConditionsTableAdapter =
      reactionConditionsTablePart != null
        ? getStructuredTableAdapters()[reactionConditionsTablePart.item.kind]
        : null;
    const reactionConditionsTableItemProps =
      reactionConditionsTablePart != null && reactionConditionsTableAdapter != null
        ? tableItemPropsByName[reactionConditionsTableAdapter.toTable(reactionConditionsTablePart.item).name]
        : null;

    const sharedContainedTableProps = {
      editor,
      editorProps: {...editorProps, locked: editorProps.locked || this.state.diagramSyncing},
      isInGroup: true as const,
      deleteHandler: this.handleDeleteGroup,
      onUpdateContent: this.handleUpdateContainedTableContent,
      overrideDefaultTableProps: {disableMaximizedTables: true, isCollapsible: false},
    };

    const isExpanded = !isCollapsed || editorProps.viewer;

    const actionFunctionParameters: GroupActionFunctionParameters<
      StoichiometryGroupMetadata,
      StoichiometryCreateGroupExtras
    > = {
      entryOrTemplateId: editorProps.fileId,
      noteLinkedGroup: group,
      noteLinkedGroupAdapter: adapter,
      locked: editorProps.locked,
      isCollapsed,
      evaluatedTableMap,
      groupResyncRequired:
        !isExpanded || !this.state.hasReceivedLiveStalenessResult || this.state.hasLiveStaleCells,
      actionFunctionContext,
      getItemWithUpdatedContent: editor.getItemWithUpdatedContent,
      getIdFromCidFn: editorProps.getIdFromCid,
      updateGroup: (noteLinkedGroup) => {
        editor.itemUpdateHandler(editor.getIndex(notePartCid), {item: noteLinkedGroup});
      },
      insertAfterGroup: (noteParts, cidIdMap) => {
        editor.props.registerObjects(cidIdMap);
        editor.itemInsertHandler(editor.getIndex(notePartCid), noteParts);
      },
      selectorsById: {},
    };

    const printableContainerClass = editorProps.viewer ? 'printable-container-scaleForPrint' : '';

    if (BenchlingConfig.CHEMISTRY_ENABLE_STOICHIOMETRY_TABLE) {
      this._enrichResolutionActions(
        stoichiometryComputedTable,
        stoichiometryTable,
        this.getUnrecognizedMoleculeResolutionAction
      );
    }

    return (
      <SelectableNotePart indentation={0} className={printableContainerClass}>
        <div className={css(styles.groupWrapper)}>
          <GroupTitleBar
            groupTypeLabel={adapter.getDisplayName(group)}
            groupName={group.name}
            icon={groupKindToIcon(group.kind)}
            rightHeaderControls={<GroupActionButtons {...actionFunctionParameters} />}
            isCollapsed={isCollapsed}
            onToggleCollapsed={onToggleCollapsed}
            viewer={editorProps.viewer}
          />
          {isExpanded && (
            <div
              role="presentation"
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onMouseOver={(e) => {
                e.stopPropagation();
              }}
            >
              <StoichiometryTableEmbeddedChemicalSketcher
                reactionSchemeFile={
                  reactionSchemePart?.item ?? this.getOrCreateEmptyReactionSchemeFile(group.name)
                }
                onUpdate={this.handleUpdate}
                onDiagramSyncingChange={this.handleDiagramSyncingChange}
                structuredTable={stoichiometryTable}
                computedTable={stoichiometryComputedTable ?? []}
                locked={editorProps.locked}
                viewer={editorProps.viewer}
              />
              {this.state.hasLiveStaleCells && !editorProps.viewer && !editorProps.locked && (
                <div className={css(styles.resyncBannerWrapper)}>
                  <ResyncReactionTableBanner
                    onResyncClick={() => refreshNoteLinkedGroup(actionFunctionParameters)}
                  />
                </div>
              )}
              {stoichiometryTableItemProps != null && (
                <StructuredTableFormOrView
                  {...sharedContainedTableProps}
                  index={group.containedNoteParts.indexOf(stoichiometryTablePart)}
                  notePart={stoichiometryTablePart as unknown as NotePart & {item: NoteLinkedStructuredTable}}
                  onLiveStalenessChange={this.handleLiveStalenessChange}
                  itemProps={stoichiometryTableItemProps}
                  adapter={stoichiometryTableAdapter}
                  {...getContainedTableSearchResultProps(this.props, stoichiometryTablePart.cid)}
                />
              )}
              {reactionConditionsTablePart != null &&
                reactionConditionsTableAdapter != null &&
                reactionConditionsTableItemProps != null && (
                  <ReactionConditionsStructuredTableFormOrView
                    {...sharedContainedTableProps}
                    index={group.containedNoteParts.indexOf(reactionConditionsTablePart)}
                    notePart={
                      reactionConditionsTablePart as unknown as NotePart & {item: NoteLinkedStructuredTable}
                    }
                    itemProps={reactionConditionsTableItemProps}
                    adapter={reactionConditionsTableAdapter}
                    {...getContainedTableSearchResultProps(this.props, reactionConditionsTablePart.cid)}
                  />
                )}
            </div>
          )}
        </div>
      </SelectableNotePart>
    );
  }
}

/**
 * Wraps {@link StructuredTableFormOrView} for the Reaction Conditions sub-table. It provides the
 * {@link LibraryParameterDefinitionsContext} that the Parameter picker reads, limited to the
 * definitions a Reaction Condition can record (see {@link filterReactionConditionParameterDefinitions}).
 */
function ReactionConditionsStructuredTableFormOrView(
  props: React.ComponentProps<typeof StructuredTableFormOrView>
): JSX.Element {
  const allDefinitions = useAllLibraryParameterDefinitions();
  const definitions = React.useMemo(
    () => filterReactionConditionParameterDefinitions(allDefinitions),
    [allDefinitions]
  );
  return (
    <LibraryParameterDefinitionsContext.Provider value={definitions}>
      <StructuredTableFormOrView {...props} />
    </LibraryParameterDefinitionsContext.Provider>
  );
}

export function StoichiometryGroupView(props: StoichiometryGroupViewProps): JSX.Element {
  const actionFunctionContext = useActionFunctionContext();

  const {isCollapsed, onToggleCollapsed} = useNotebookItemCollapse(
    generateNoteLinkedObjectCollapseId(props.editorProps.fileId, props.group.provenanceUuid)
  );

  return (
    <StoichiometryGroupInnerView
      {...props}
      actionFunctionContext={actionFunctionContext}
      isCollapsed={isCollapsed}
      onToggleCollapsed={onToggleCollapsed}
    />
  );
}

const styles = StyleSheet.create({
  groupWrapper: {
    backgroundColor: 'white',
    border: `1px solid ${GRAY}`,
    borderRadius: '4px',
    display: 'inline-block',
    maxWidth: '100%',
    position: 'relative',
    width: '100%',
  },
  resyncBannerWrapper: {
    padding: '12px 10px 0 10px',
  },
});
